import { Fragment } from 'react';
import { clsx } from 'clsx';

import {
  css,
  styled,
  TableCell as MuiTableCell,
  tableCellClasses as muiTableCellClasses,
} from '@mui/material';
import { np } from '@kc-console/core';

import { tableRowClasses } from '../TableRow';
import { TableSortLabel } from '../TableSortLabel';
import { tableCellClasses } from './theme';

import type { TableCellProps } from './types';

const capitalize = function <V extends string>(str: V): Capitalize<V> {
  return (str.charAt(0).toUpperCase() + str.slice(1)) as Capitalize<V>;
};

const TableCell = (props: TableCellProps) => {
  // 마이그레이션된 패턴: useThemeProps 제거, 기본값을 컴포넌트에서 직접 처리
  const {
    startAdornment,
    endAdornment,
    children,
    useSortLabel = false,
    TableSortLabelComponent = TableSortLabel,
    tableSortLabelComponentProps = {},
    resizable,
    resizeHandler,
    resizeResetHandler,
    isResizing,
    slots,
    slotProps,
    sortDirection,
    sticky = false, // theme defaultProps에서 이동
    stickyPosition = 'left', // theme defaultProps에서 이동
    ...others
  } = props;

  const { content: ContentSlotComponent } = slots ?? {};
  const { content: contentSlotProps } = slotProps ?? {};

  const ownerState: TableCellProps = {
    ...props,
    sticky,
    stickyPosition,
  };

  // 동적 클래스 생성 (useUtilityClasses 대체)
  const getDynamicClasses = () => {
    return {
      root: clsx(
        // tableCellClasses.root는 target으로 자동 적용되므로 제외
        sticky && tableCellClasses.sticky,
        sticky && tableCellClasses[`sticky${capitalize(stickyPosition)}`],
      ),
    };
  };

  const dynamicClasses = getDynamicClasses();

  const TableCellInner = !useSortLabel ? Fragment : TableSortLabelComponent;

  return (
    <MuiTableCell {...others}>
      <TableCellRoot ownerState={ownerState} className={dynamicClasses.root}>
        <TableCellInner
          {...(!useSortLabel ? {} : { ...tableSortLabelComponentProps, direction: sortDirection })}
        >
          {startAdornment && (
            <TableCellStartAdornment ownerState={ownerState}>
              {startAdornment}
            </TableCellStartAdornment>
          )}
          {children && (
            <TableCellContent
              as={ContentSlotComponent}
              ownerState={ownerState}
              {...contentSlotProps}
            >
              {children}
            </TableCellContent>
          )}
          {endAdornment && (
            <TableCellEndAdornment ownerState={ownerState}>{endAdornment}</TableCellEndAdornment>
          )}
        </TableCellInner>
      </TableCellRoot>
      {resizable && (
        <TableCellResizeHandle
          onMouseDown={resizeHandler}
          onTouchStart={resizeHandler}
          onDoubleClick={resizeResetHandler}
          ownerState={ownerState}
        />
      )}
      {isResizing && <TableCellResizingIndicator ownerState={ownerState} />}
    </MuiTableCell>
  );
};

TableCell.displayName = 'TableCell';

export default TableCell;

// Styled Components - 하이브리드 패턴
const TableCellRoot = styled('div', {
  target: tableCellClasses.root,
})<{ ownerState: TableCellProps }>(({ ownerState }) => {
  return css`
    display: flex;
    align-items: center;
    justify-content: ${ownerState.align};

    .${tableRowClasses.collapse} > & {
      display: block;
    }
  `;
});

const TableCellContent = styled('span', {
  target: tableCellClasses.content,
  shouldForwardProp: np('ownerState', 'as'),
})<{ ownerState: TableCellProps }>`
  &:focus-within {
    margin: -4px;
    padding: 4px;
  }

  .${tableRowClasses.collapse} &:focus-within {
    margin: 0;
    padding: 0;
  }

  .${tableRowClasses.collapse} & {
    display: block;
    width: 100%;
  }
`;

const TableCellStartAdornment = styled('div', {
  target: tableCellClasses.startAdornment,
})<{ ownerState: TableCellProps }>`
  display: inline-flex;

  .${muiTableCellClasses.head} & {
    margin-right: 4px;
  }

  .${muiTableCellClasses.body} & {
    margin-right: 8px;
  }
`;

const TableCellEndAdornment = styled('div', {
  target: tableCellClasses.endAdornment,
})<{ ownerState: TableCellProps }>`
  display: inline-flex;

  .${muiTableCellClasses.head} & {
    margin-left: 4px;
  }

  .${muiTableCellClasses.body} & {
    margin-left: 8px;
  }
`;

const TableCellResizeHandle = styled('div', {
  target: tableCellClasses.resizeHandle,
})<{ ownerState: TableCellProps }>(({ theme }) => {
  return css`
    position: absolute;
    top: 0;
    right: 0;
    width: 4px;
    height: 100%;
    cursor: col-resize;
    z-index: ${theme.zIndex.raised};
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;
    touch-action: none;

    &::after {
      content: '';
      display: block;
      width: 1px;
      height: 18px;
      background-color: ${theme.color['border/weaker/default']};
    }

    &:hover::after {
      background-color: ${theme.color['border/weak/hover']};
    }

    &:active::after {
      background-color: ${theme.color['border/weak/press']};
    }
  `;
});

const TableCellResizingIndicator = styled('div', {
  target: tableCellClasses.resizingIndicator,
})<{ ownerState: TableCellProps }>(({ theme }) => {
  return css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &::after {
      content: '';
      display: block;
      width: 1px;
      height: 100%;
      border-right: ${theme.mixins.border('border/weak/hover', 1, 'dashed')};
    }

    body:has(&) {
      cursor: col-resize;
    }
  `;
});
